import React, { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  Building, 
  FileText, 
  Calendar,
  MessageSquare,
  Plus,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  Paperclip,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { StatusCandidato } from '../types/rh';
import NovaEntrevistaModal from './NovaEntrevistaModal';
import DocumentViewer from './DocumentViewer';

interface CandidatoDetalhesModalProps {
  candidatoId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface CandidatoCompleto {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  curriculo_url: string | null;
  vaga_id: string | null;
  origem_lead: string | null;
  data_envio: string;
  status: string;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
  tipo_cadastro: string;
  vaga?: {
    id: string;
    titulo: string;
    localidade: string;
    tipo_localidade: string;
  } | null;
}

interface ConversaIA {
  id: string;
  mensagem: string;
  tipo: 'usuario' | 'ia';
  timestamp: string;
  metadata?: any;
}

interface TimelineItem {
  id: string;
  titulo: string;
  descricao: string | null;
  tipo: string;
  criado_por: string | null;
  created_at: string;
  metadata?: any;
}

interface DocumentoCandidato {
  id: string;
  nome_arquivo: string;
  nome_original: string;
  url_arquivo: string;
  tipo_documento: string;
  tamanho_bytes: number | null;
  tipo_mime: string | null;
  created_at: string;
}

export const CandidatoDetalhesModal: React.FC<CandidatoDetalhesModalProps> = ({
  candidatoId,
  isOpen,
  onClose
}) => {
  const [candidato, setCandidato] = useState<CandidatoCompleto | null>(null);
  const [conversasIA, setConversasIA] = useState<ConversaIA[]>([]);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [documentos, setDocumentos] = useState<DocumentoCandidato[]>([]);
  const [loading, setLoading] = useState(false);
  const [novaAtualizacao, setNovaAtualizacao] = useState('');
  const [adicionandoAtualizacao, setAdicionandoAtualizacao] = useState(false);
  const [mostrarFormAtualizacao, setMostrarFormAtualizacao] = useState(false);
  const [mostrarUploadDocumento, setMostrarUploadDocumento] = useState(false);
  const [showNovaEntrevistaModal, setShowNovaEntrevistaModal] = useState(false);
  const [documentoVisualizacao, setDocumentoVisualizacao] = useState<DocumentoCandidato | null>(null);
  const { toast } = useToast();

  const atualizarStatus = async (candidatoId: string, novoStatus: string) => {
    try {
      const { error } = await supabase
        .from('candidatos_rh')
        .update({ status: novoStatus as StatusCandidato })
        .eq('id', candidatoId);

      if (error) throw error;
      
      // Atualizar o estado local
      setCandidato(prev => prev ? { ...prev, status: novoStatus } : null);

      toast({
        title: "Status atualizado",
        description: `Status alterado para: ${novoStatus.replace('_', ' ')}`,
        variant: "default"
      });

    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar status do candidato",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (isOpen && candidatoId) {
      carregarDadosCandidato();
    }
  }, [isOpen, candidatoId]);

  const carregarDadosCandidato = async () => {
    if (!candidatoId) return;

    setLoading(true);
    try {
      // Carregar dados do candidato
      const { data: candidatoData, error: candidatoError } = await supabase
        .from('candidatos_rh')
        .select(`
          *,
          vaga:vagas_rh (
            id,
            titulo,
            localidade,
            tipo_localidade
          )
        `)
        .eq('id', candidatoId)
        .single();

      if (candidatoError) throw candidatoError;
      setCandidato(candidatoData);

      // Carregar conversas da IA
      const { data: conversasData, error: conversasError } = await supabase
        .from('conversas_ia_rh')
        .select('*')
        .eq('candidato_id', candidatoId)
        .order('timestamp', { ascending: true });

      if (conversasError) {
        console.error('Erro ao carregar conversas:', conversasError);
      } else {
        setConversasIA((conversasData || []).map(conversa => ({
          ...conversa,
          tipo: conversa.tipo as 'usuario' | 'ia'
        })));
      }

      // Carregar timeline
      const { data: timelineData, error: timelineError } = await supabase
        .from('timeline_candidatos')
        .select('*')
        .eq('candidato_id', candidatoId)
        .order('created_at', { ascending: false });

      if (timelineError) {
        console.error('Erro ao carregar timeline:', timelineError);
      } else {
        setTimeline(timelineData || []);
      }

      // Carregar documentos
      const { data: documentosData, error: documentosError } = await supabase
        .from('documentos_candidatos')
        .select('*')
        .eq('candidato_id', candidatoId)
        .order('created_at', { ascending: false });

      if (documentosError) {
        console.error('Erro ao carregar documentos:', documentosError);
      } else {
        setDocumentos(documentosData || []);
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do candidato",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const adicionarAtualizacao = async () => {
    if (!candidatoId || !novaAtualizacao.trim()) return;

    setAdicionandoAtualizacao(true);
    try {
      const { error } = await supabase
        .from('timeline_candidatos')
        .insert({
          candidato_id: candidatoId,
          titulo: 'Atualizacao manual',
          descricao: novaAtualizacao,
          tipo: 'atualizacao_manual',
          criado_por: 'Usuário do sistema'
        });

      if (error) throw error;

      setNovaAtualizacao('');
      setMostrarFormAtualizacao(false);
      carregarDadosCandidato(); // Recarregar dados

      toast({
        title: "Atualização adicionada",
        description: "A atualização foi adicionada à timeline",
        variant: "default"
      });

    } catch (error) {
      console.error('Erro ao adicionar atualização:', error);
      toast({
        title: "Erro",
        description: "Erro ao adicionar atualização",
        variant: "destructive"
      });
    } finally {
      setAdicionandoAtualizacao(false);
    }
  };

  const baixarCurriculo = () => {
    if (candidato?.curriculo_url) {
      console.log('🔍 Baixando currículo do modal:', candidato.curriculo_url);
      
      const link = document.createElement('a');
      link.href = candidato.curriculo_url;
      link.download = `curriculo-${candidato.nome.replace(/\s+/g, '-')}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Download do modal iniciado');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'novo':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'triagem':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'entrevista_marcada':
        return <Calendar className="h-4 w-4 text-purple-500" />;
      case 'contratado':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'reprovado':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'novo':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'triagem':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'entrevista_marcada':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'contratado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'reprovado':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getOrigemIcon = (origem: string | null) => {
    switch (origem) {
      case 'chat_ia':
        return '🤖';
      case 'manual':
        return '✏️';
      case 'linkedin':
        return '💼';
      case 'indeed':
        return '🔍';
      case 'whatsapp':
        return '💬';
      default:
        return '📄';
    }
  };

  const formatarData = (data: string) => {
    return format(new Date(data), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const handleUploadDocumento = async (file: File) => {
    if (!candidatoId) return;

    // Validar arquivo
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Arquivo inválido",
        description: "Formatos aceitos: PDF, Word, JPG, PNG",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      toast({
        title: "Arquivo muito grande",
        description: "O arquivo deve ter no máximo 10MB",
        variant: "destructive"
      });
      return;
    }

    try {
      const fileName = `${candidatoId}-${Date.now()}-${file.name}`;
      
      // Upload para o storage
      const { error: uploadError } = await supabase.storage
        .from('curriculos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Obter URL do arquivo
      const { data: urlData } = supabase.storage
        .from('curriculos')
        .getPublicUrl(fileName);

      // Salvar na tabela de documentos
      const { error: dbError } = await supabase
        .from('documentos_candidatos')
        .insert({
          candidato_id: candidatoId,
          nome_arquivo: fileName,
          nome_original: file.name,
          url_arquivo: urlData.publicUrl,
          tipo_documento: file.type.includes('pdf') ? 'pdf' : file.type.includes('image') ? 'imagem' : 'documento',
          tamanho_bytes: file.size,
          tipo_mime: file.type,
          uploaded_by: 'Usuário do sistema'
        });

      if (dbError) throw dbError;

      // Adicionar entrada na timeline
      await supabase
        .from('timeline_candidatos')
        .insert({
          candidato_id: candidatoId,
          titulo: 'Documento adicionado',
          descricao: `Arquivo "${file.name}" foi adicionado`,
          tipo: 'documento_adicionado',
          criado_por: 'Usuário do sistema'
        });

      toast({
        title: "Documento enviado",
        description: "O documento foi enviado com sucesso",
        variant: "default"
      });

      setMostrarUploadDocumento(false);
      carregarDadosCandidato(); // Recarregar dados

    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast({
        title: "Erro",
        description: "Erro ao enviar documento",
        variant: "destructive"
      });
    }
  };

  const handleDeletarDocumento = async (documentoId: string) => {
    if (!confirm('Tem certeza que deseja deletar este documento?')) return;

    try {
      const { error } = await supabase
        .from('documentos_candidatos')
        .delete()
        .eq('id', documentoId);

      if (error) throw error;

      toast({
        title: "Documento removido",
        description: "O documento foi removido com sucesso",
        variant: "default"
      });

      carregarDadosCandidato(); // Recarregar dados

    } catch (error) {
      console.error('Erro ao deletar documento:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover documento",
        variant: "destructive"
      });
    }
  };

  const handleEntrevistaSuccess = () => {
    setShowNovaEntrevistaModal(false);
    carregarDadosCandidato(); // Recarregar dados para atualizar timeline
  };

  if (!isOpen || !candidatoId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <User className="h-5 w-5" />
            Detalhes do Candidato
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : candidato ? (
          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informações do Candidato */}
            <div className="space-y-6">
              {/* Dados Pessoais */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Informações Pessoais
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{candidato.nome}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{candidato.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{candidato.telefone}</span>
                  </div>
                </div>
              </div>

              {/* Status e Vaga */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Status e Vaga
                </h3>
                 <div className="space-y-3">
                   <div className="flex items-center gap-4">
                     <div className="flex items-center gap-2">
                       {getStatusIcon(candidato.status)}
                       <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(candidato.status)}`}>
                         {candidato.status.replace('_', ' ').toUpperCase()}
                       </span>
                     </div>
                     <select
                       value={candidato.status}
                       onChange={(e) => atualizarStatus(candidato.id, e.target.value)}
                       className="text-sm border border-border rounded-md px-2 py-1 bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                     >
                       <option value="novo">Novo</option>
                       <option value="triagem">Em triagem</option>
                       <option value="entrevista_marcada">Entrevista marcada</option>
                       <option value="reprovado">Reprovado</option>
                        <option value="contratado">Contratado</option>
                      </select>
                    </div>
                   {candidato.vaga && (
                     <div className="flex items-center gap-2">
                       <Building className="h-4 w-4 text-muted-foreground" />
                       <span>{candidato.vaga.titulo} - {candidato.vaga.localidade}</span>
                     </div>
                   )}
                   <div className="flex items-center gap-2">
                     <span className="text-sm">{getOrigemIcon(candidato.origem_lead)}</span>
                     <span className="text-sm text-muted-foreground">
                       Origem: {candidato.origem_lead || 'Não informado'}
                     </span>
                   </div>
                   
                   {/* Botão Agendar Entrevista */}
                   <div className="pt-3 border-t border-border">
                     <Button
                       onClick={() => setShowNovaEntrevistaModal(true)}
                       className="w-full flex items-center gap-2"
                       variant="default"
                     >
                       <Calendar className="h-4 w-4" />
                       Agendar Entrevista
                     </Button>
                   </div>
                </div>
              </div>

              {/* Documentos */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Documentos
                  </h3>
                  <Button
                    onClick={() => setMostrarUploadDocumento(!mostrarUploadDocumento)}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar
                  </Button>
                </div>

                {/* Formulário de Upload */}
                {mostrarUploadDocumento && (
                  <div className="mb-4 p-3 border border-border rounded-lg bg-background">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadDocumento(file);
                      }}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-muted file:text-muted-foreground"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Formatos aceitos: PDF, Word, JPG, PNG (máximo 10MB)
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {/* Currículo Original */}
                  {candidato.curriculo_url && (
                    <div className="flex items-center justify-between p-3 border border-border rounded bg-background">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Currículo Principal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => setDocumentoVisualizacao({
                            id: 'curriculo-principal',
                            nome_arquivo: 'curriculo-principal',
                            nome_original: 'Currículo Principal',
                            url_arquivo: candidato.curriculo_url!,
                            tipo_documento: 'currículo',
                            tipo_mime: candidato.curriculo_url!.toLowerCase().includes('.pdf') ? 'application/pdf' : null,
                            tamanho_bytes: null,
                            created_at: candidato.created_at
                          })}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Visualizar
                        </Button>
                        <Button
                          onClick={baixarCurriculo}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Baixar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Documentos Adicionais */}
                  {documentos.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border border-border rounded bg-background">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <span className="text-sm font-medium">{doc.nome_original}</span>
                          <p className="text-xs text-muted-foreground">
                            {formatarData(doc.created_at)} • {doc.tamanho_bytes ? `${Math.round(doc.tamanho_bytes / 1024)} KB` : ''}
                          </p>
                        </div>
                      </div>
                       <div className="flex items-center gap-2">
                         <Button
                           onClick={() => setDocumentoVisualizacao(doc)}
                           variant="outline"
                           size="sm"
                           className="flex items-center gap-2"
                         >
                           <Eye className="h-4 w-4" />
                           Visualizar
                         </Button>
                          <Button
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = doc.url_arquivo;
                              link.download = `${doc.nome_original}`;
                              link.target = '_blank';
                              link.rel = 'noopener noreferrer';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                           variant="outline"
                           size="sm"
                           className="flex items-center gap-2"
                         >
                           <Download className="h-4 w-4" />
                           Baixar
                         </Button>
                         <Button
                           onClick={() => handleDeletarDocumento(doc.id)}
                           variant="outline"
                           size="sm"
                           className="flex items-center gap-2 text-red-600 hover:text-red-700"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                    </div>
                  ))}

                  {documentos.length === 0 && !candidato.curriculo_url && (
                    <div className="text-center py-4 text-muted-foreground">
                      <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhum documento anexado</p>
                    </div>
                  )}
                </div>

                {/* Observações */}
                {candidato.observacoes && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <h4 className="text-sm font-medium text-foreground mb-2">Observações:</h4>
                    <p className="text-sm text-muted-foreground bg-background p-3 rounded border">
                      {candidato.observacoes}
                    </p>
                  </div>
                )}
              </div>

              {/* Conversa com IA */}
              {conversasIA.length > 0 && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Conversa com IA
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {conversasIA.map((conversa) => (
                      <div
                        key={conversa.id}
                        className={`p-3 rounded-lg ${
                          conversa.tipo === 'usuario' 
                            ? 'bg-primary/10 ml-4' 
                            : 'bg-secondary/10 mr-4'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium">
                            {conversa.tipo === 'usuario' ? 'Candidato' : 'IA'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatarData(conversa.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm">{conversa.mensagem}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Timeline
                </h3>
                <Button
                  onClick={() => setMostrarFormAtualizacao(!mostrarFormAtualizacao)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar
                </Button>
              </div>

              {/* Formulário para nova atualização */}
              {mostrarFormAtualizacao && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <textarea
                    value={novaAtualizacao}
                    onChange={(e) => setNovaAtualizacao(e.target.value)}
                    placeholder="Descreva a atualização..."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground mb-3"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={adicionarAtualizacao}
                      disabled={adicionandoAtualizacao || !novaAtualizacao.trim()}
                      size="sm"
                    >
                      {adicionandoAtualizacao ? 'Adicionando...' : 'Adicionar'}
                    </Button>
                    <Button
                      onClick={() => {
                        setMostrarFormAtualizacao(false);
                        setNovaAtualizacao('');
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Lista da Timeline */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {timeline.map((item, index) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 bg-primary rounded-full"></div>
                      {index < timeline.length - 1 && (
                        <div className="w-0.5 h-8 bg-border mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="bg-background border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-foreground">
                            {item.titulo}
                          </h4>
                          <span className="text-xs text-muted-foreground">
                            {formatarData(item.created_at)}
                          </span>
                        </div>
                        {item.descricao && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {item.descricao}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-secondary/50 px-2 py-1 rounded">
                            {item.tipo}
                          </span>
                          {item.criado_por && (
                            <span className="text-xs text-muted-foreground">
                              por {item.criado_por}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {timeline.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma atualização ainda</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Candidato não encontrado</p>
          </div>
        )}

        <div className="flex justify-end p-6 border-t border-border">
          <Button onClick={onClose} variant="outline">
            Fechar
          </Button>
        </div>

        {/* Modal de Nova Entrevista */}
        {candidato && (
          <NovaEntrevistaModal
            isOpen={showNovaEntrevistaModal}
            onClose={() => setShowNovaEntrevistaModal(false)}
            onSuccess={handleEntrevistaSuccess}
            candidatoPredefinido={{
              id: candidato.id,
              nome: candidato.nome,
              email: candidato.email,
              vaga_id: candidato.vaga_id
            }}
          />
        )}

        {/* Visualizador de Documentos */}
        {documentoVisualizacao && (
          <DocumentViewer
            isOpen={!!documentoVisualizacao}
            onClose={() => setDocumentoVisualizacao(null)}
            document={documentoVisualizacao}
          />
        )}
      </div>
    </div>
  );
};